import React, { FC, Suspense, use, useCallback, useRef, useState } from 'react';

import * as ReactDOM from 'react-dom/client';

import { Branding } from './components/branding';
import { WidgetFailed, WidgetLoading } from './components/loading';
import { Widget } from './components/widget/widget';
import { DataPlaneMerchant, StyleState } from '../src/@types/types';
import ErrorBoundary from './components/error/error-boundary';
import { getNewRelicAgent, initNewRelicAgent } from './newrelic';
import './i18n'; // import i18n needs to be bundled
import { WidgetErrors } from './shared/constants';
import { getErrorMessage } from './shared/utils';

const BROWSER_STYLESHEET_LOAD_DELAY = 10;

const fetchMerchantConfig = async (handle: string): Promise<DataPlaneMerchant> =>
	fetch(`${APP_SETTINGS.dataPlaneUrl}${handle}.json`)
		.then(res => (res.ok ? res.json() : Promise.reject(res)))
		.catch(err => {
			const agent = getNewRelicAgent();
			agent?.noticeError(WidgetErrors.merchantConfigFailed, {
				handle,
				error: err,
			});
			return Promise.reject(err);
		});

const Root: FC<{
	handle: string;
	merchantConfig: Promise<DataPlaneMerchant>;
	stylesheetUrl?: string;
}> = ({ stylesheetUrl, merchantConfig, handle }) => {
	const styleRef = useRef<HTMLLinkElement>(null);
	const configData = use(merchantConfig);
	const isDev = APP_SETTINGS.env === 'dev';
	const [stylesState, setStylesState] = useState<StyleState>(isDev ? StyleState.loaded : StyleState.idle);

	const {
		Branding: { PrimaryColor, SecondaryColor },
		MerchantSettings: { PaymentLabel = 'give' },
		Funds,
	} = configData;

	const handleStylesheet = useCallback((val: StyleState) => {
		const timer = setTimeout(() => {
			setStylesState(val);
		}, BROWSER_STYLESHEET_LOAD_DELAY);
		return () => clearTimeout(timer);
	}, []);

	if (!Funds.length) throw new Error('No Merchant funds available');

	return (
		<ErrorBoundary
			fallback={
				<WidgetFailed paymentLabel={PaymentLabel} redirectUrl={`${APP_SETTINGS.webGivingUrl}${handle}`} />
			}
		>
			{stylesheetUrl && (
				<link
					ref={styleRef}
					href={stylesheetUrl}
					rel="stylesheet"
					onError={() => handleStylesheet(StyleState.failed)}
					onLoad={() => handleStylesheet(StyleState.loaded)}
				/>
			)}
			<Branding primaryColor={PrimaryColor} secondaryColor={SecondaryColor || PrimaryColor} />
			{stylesState === StyleState.loaded ? <Widget config={configData} handle={handle} /> : <WidgetLoading />}
		</ErrorBoundary>
	);
};

try {
	initNewRelicAgent();

	// check for fetchAPI support
	if (!window.fetch) {
		throw new Error('Fetch API not supported');
	}

	const fallbackDivId = 'pushpay-embedded-giving-fallback';
	const positioningContainer = document.getElementById('pushpay-embedded-giving');
	const fallbackContainer = document.getElementById(fallbackDivId);
	let root = positioningContainer || fallbackContainer;

	if (!root) {
		const embeddedContainer = document.createElement('div');
		embeddedContainer.id = fallbackDivId;
		root = embeddedContainer;
		document.body.appendChild(embeddedContainer);
	}

	const shadowRoot = root.attachShadow({ mode: 'open' });
	const merchantHandle = (window.pushpayEmbeddedConfig || { handle: 'undefined' }).handle;

	if (shadowRoot) {
		ReactDOM.createRoot(shadowRoot).render(
			<React.StrictMode>
				<Suspense fallback={<WidgetLoading />}>
					<ErrorBoundary fallback={<WidgetFailed />}>
						<Root
							handle={merchantHandle}
							merchantConfig={fetchMerchantConfig(merchantHandle)}
							stylesheetUrl={EMBEDDED_STYLESHEET}
						/>
					</ErrorBoundary>
				</Suspense>
			</React.StrictMode>
		);
	} else {
		throw new Error('Shadow root not found');
	}
} catch (error) {
	const agent = getNewRelicAgent();
	agent?.noticeError(WidgetErrors.failedToRender, {
		message: getErrorMessage(error),
		error,
	});
}
